import type { BrandSettings } from '@quickreport/domain';
import { buildReportData, qrSvg, type SignatoryInfo, sampleReportContent } from '@quickreport/pdf';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { extensionOf, type StoredImage } from '../../local-db/files';
import { renderPdf } from '../../pdf/client';

const PdfPages = lazy(() => import('../../pdf/PdfPages').then((m) => ({ default: m.PdfPages })));

export interface PreviewSignatory extends SignatoryInfo {
  image: StoredImage | null;
}

/** Live sample PDF built from the settings being edited. Rendered on this device. */
export function BrandPreview({
  brand,
  logo,
  background,
  signatories,
  onPdf,
}: {
  brand: BrandSettings;
  logo: StoredImage | null;
  background?: StoredImage | null;
  signatories: PreviewSignatory[];
  onPdf?: (pdf: Uint8Array | null) => void;
}) {
  const [pdf, setPdf] = useState<Uint8Array | null>(null);
  const [status, setStatus] = useState('Starting the PDF engine…');
  const [error, setError] = useState<string | null>(null);
  const ticket = useRef(0);

  useEffect(() => {
    const mine = ++ticket.current;
    const timer = setTimeout(async () => {
      const assets: Record<string, Uint8Array> = {};
      let logoPath: string | undefined;
      if (logo) {
        logoPath = `/assets/logo.${extensionOf(logo.mime)}`;
        assets[logoPath] = logo.bytes;
      }
      let backgroundPath: string | undefined;
      if (background) {
        backgroundPath = `/assets/background.${extensionOf(background.mime)}`;
        assets[backgroundPath] = background.bytes;
      }
      const sigs: SignatoryInfo[] = signatories.map((s) => {
        if (!s.image)
          return {
            id: s.id,
            name: s.name,
            qualification: s.qualification,
            designation: s.designation,
            regNo: s.regNo,
          };
        const path = `/assets/sig-${s.id}.${extensionOf(s.image.mime)}`;
        assets[path] = s.image.bytes;
        return {
          id: s.id,
          name: s.name,
          qualification: s.qualification,
          designation: s.designation,
          regNo: s.regNo,
          signaturePath: path,
        };
      });
      const shown = sigs.length
        ? sigs
        : [
            {
              id: 'sample',
              name: 'Dr. PQR',
              qualification: 'MD (Pathology)',
              designation: 'Consultant Pathologist',
            },
          ];
      const data = buildReportData({
        labNo: '2026-000001',
        content: sampleReportContent(shown.map((s) => s.id)),
        brand,
        signatories: shown,
        ...(logoPath ? { logoPath } : {}),
        ...(backgroundPath ? { backgroundPath } : {}),
        // A real report carries a QR code, which makes the patient box taller: show it here too.
        verify: { code: '7KQ2M9XAPT', qrPath: '/assets/qr.svg' },
        signedAt: new Date(),
        status: 'final',
      });
      assets['/assets/qr.svg'] = await qrSvg('https://verify.example/r/7KQ2M9XAPT');
      const res = await renderPdf(data, assets);
      if (mine !== ticket.current) return;
      if (res.error || !res.pdf) {
        setError(res.error ?? 'The sample could not be made');
        return;
      }
      setError(null);
      setStatus(`Sample rendered in ${res.ms.toFixed(0)} ms`);
      setPdf(res.pdf);
      onPdf?.(res.pdf);
    }, 400);
    return () => clearTimeout(timer);
  }, [brand, logo, background, signatories, onPdf]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-canvas">
      <div className="flex h-8 items-center px-3 text-[12px] text-slate" role="status">
        {error ? <span className="text-flag">{error}</span> : status}
      </div>
      {pdf ? (
        <Suspense
          fallback={<div className="grid flex-1 place-items-center text-slate">Loading…</div>}
        >
          <PdfPages pdf={pdf} />
        </Suspense>
      ) : (
        <div className="grid flex-1 place-items-center text-slate">Loading preview…</div>
      )}
    </div>
  );
}
